/**
 * Trip Import Controller
 *
 * Handles complete trip import functionality from ZIP archives and JSON files.
 * Provides comprehensive import capabilities with data validation, conflict resolution,
 * and user-friendly feedback throughout the import process.
 *
 * Features:
 * - ZIP archive import with full trip data restoration
 * - JSON file import for trip data
 * - Data validation and integrity checking
 * - Conflict resolution for existing trips
 * - Progress tracking and user feedback
 * - Rollback capabilities on import failures
 */

// Import ZipFormatProcessor for binary ZIP extraction
let zipProcessor = null;

// Async loading function to avoid circular dependencies
async function loadZipProcessor() {
  if (!zipProcessor) {
    try {
      const { default: processor } = await import('@trip-planner-monorepo/app-features');
      zipProcessor = processor.zipProcessor || processor;
      console.log('[TripImport] ZIP processor loaded successfully');
    } catch (error) {
      console.error('[TripImport] Failed to load ZIP processor:', error);
      throw new Error('ZIP extraction functionality not available');
    }
  }
  return zipProcessor;
}

// Import Firebase services for direct database operations
import { db, storage } from '@trip-planner-monorepo/shared';
// eslint-disable-next-line import/named
import { collection, doc, addDoc, getDoc, writeBatch } from 'firebase/firestore';
// eslint-disable-next-line import/named
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

class TripImportController {
  constructor() {
    this.supportedFormats = ['zip', 'json'];
    this.maxFileSize = 50 * 1024 * 1024; // 50MB limit
    this.supportedVersions = ['1.0.0'];
  }

  /**
   * Convert Firestore Timestamp-like objects to ISO strings
   * Handles serialized Timestamps from JSON export
   *
   * @param {any} value - Date value in various formats
   * @returns {string|null} ISO string or original value
   */
  convertTimestampToISOString(value) {
    // Already a string (YYYY-MM-DD or ISO format)
    if (typeof value === 'string') {
      return value;
    }

    // Null or undefined - return null (Firestore doesn't accept undefined)
    if (!value) {
      return null;
    }

    // Firestore Timestamp-like object {seconds, nanoseconds}
    if (value.seconds !== undefined) {
      const date = new Date(value.seconds * 1000 + (value.nanoseconds || 0) / 1000000);
      return date.toISOString();
    }

    // Unknown format - return null and log warning
    console.warn('[TripImport] Unknown date format:', value);
    return null;
  }

  /**
   * Show trip import modal with drag-and-drop interface
   *
   * @param {Object} context - Current application context
   * @returns {Promise<void>} Modal display (async)
   */
  async showTripImportModal(context) {
    try {
      // Import the modal component dynamically
      const { TripImportModal } = await import('@trip-planner-monorepo/app-features');

      // Create and show modal
      return this.renderModal(TripImportModal, context);
    } catch (error) {
      console.error('Failed to load trip import modal:', error);
      // Fallback to legacy file selection
      return await this.initiateImport(context);
    }
  }

  /**
   * Render modal component (to be implemented by framework integration)
   *
   * @param {Component} ModalComponent - React modal component
   * @param {Object} context - Application context
   * @returns {Promise<void>} Modal rendering promise
   */
  async renderModal(ModalComponent, context) {
    // This method should be implemented by the framework integration
    console.log('Rendering TripImportModal with context:', {
      userId: context.user?.uid,
      currentTripId: context.tripId,
      hasCurrentTrip: !!(context.trip && context.tripId)
    });

    // In a real integration, this would:
    // 1. Create a modal instance with React.createElement
    // 2. Render it using ReactDOM or framework renderer
    // 3. Pass context as props
    // 4. Handle modal events (import, cancel, close)
    // 5. Return a promise that resolves when modal closes

    // For now, fallback to legacy import
    return await this.initiateImport(context);
  }

  /**
   * Initiate trip import process (legacy method)
   *
   * @param {Object} context - Current application context
   * @param {Object} context.user - Current user data
   * @returns {Promise<boolean>} Success status
   */
  async initiateImport(context) {
    try {
      console.log('Initiating trip import...');

      // Validate context
      if (!this.validateImportContext(context)) {
        throw new Error('Invalid import context - user authentication required');
      }

      // Show file selection dialog
      const file = await this.showFileSelectionDialog();

      if (!file) {
        console.log('Import cancelled by user');
        return false;
      }

      // Validate file
      const validation = await this.validateImportFile(file);
      if (!validation.valid) {
        throw new Error(`Invalid import file: ${validation.error}`);
      }

      // Show import progress
      this.showImportStartedNotification(file.name);

      // Process the import
      const result = await this.processImport(file, context);

      if (result.success) {
        this.showImportCompletedNotification(result);
        return true;
      } else {
        throw new Error(result.error || 'Import failed');
      }
    } catch (error) {
      console.error('Trip Import failed:', error);
      this.showImportErrorNotification(error.message);
      return false;
    }
  }

  /**
   * Show file selection dialog for import
   *
   * @returns {Promise<File|null>} Selected file or null if cancelled
   */
  async showFileSelectionDialog() {
    return new Promise((resolve) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.zip,.json';
      input.multiple = false;

      input.onchange = (event) => {
        const file = event.target.files[0];
        resolve(file);
      };

      input.oncancel = () => {
        resolve(null);
      };

      // Trigger file selection dialog
      input.click();
    });
  }

  /**
   * Validate import file
   *
   * @param {File} file - Selected file
   * @returns {Promise<Object>} Validation result
   */
  async validateImportFile(file) {
    console.log('Validating import file...');

    // Check file size
    if (file.size > this.maxFileSize) {
      return {
        valid: false,
        error: `File too large. Maximum size is ${Math.round(this.maxFileSize / 1024 / 1024)}MB`
      };
    }

    // Check file type
    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (!this.supportedFormats.includes(fileExtension)) {
      return {
        valid: false,
        error: `Unsupported file format. Supported formats: ${this.supportedFormats.join(', ')}`
      };
    }

    // Basic file structure validation would go here
    // For now, assume valid if it passes basic checks
    return { valid: true };
  }

  /**
   * Process the import based on file type
   *
   * @param {File} file - Import file
   * @param {Object} context - Application context
   * @returns {Promise<Object>} Import result
   */
  async processImport(file, context) {
    const fileExtension = file.name.split('.').pop().toLowerCase();

    try {
      if (fileExtension === 'zip') {
        return await this.processZipImport(file, context);
      } else if (fileExtension === 'json') {
        return await this.processJsonImport(file, context);
      } else {
        throw new Error(`Unsupported file type: ${fileExtension}`);
      }
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Process ZIP file import
   *
   * @param {File} file - ZIP file
   * @param {Object} context - Application context
   * @returns {Promise<Object>} Import result
   */
  async processZipImport(file, context) {
    console.log('[TripImport] Processing ZIP import...');

    try {
      // Extract ZIP using real binary ZIP processor
      const importData = await this.parseZipContent(file);

      console.log('[TripImport] ZIP data extracted successfully:', {
        tripName: importData.trip?.name,
        daysCount: importData.days?.length || 0,
        placesCount: importData.places?.length || 0,
        accommodationCount: importData.accommodation?.length || 0,
        travelCount: importData.travel?.length || 0,
        documentsCount: importData.documents?.length || 0
      });

      // Validate import data
      const validation = await this.validateImportData(importData);
      if (!validation.valid) {
        throw new Error(`Invalid import data: ${validation.error}`);
      }

      // TODO: Check for conflicts (Phase 4 feature)
      // const conflicts = await this.checkForConflicts(importData, context);
      // if (conflicts.hasConflicts) {
      //   const resolution = await this.resolveConflicts(conflicts, context);
      //   if (!resolution.proceed) {
      //     return {
      //       success: false,
      //       error: 'Import cancelled due to conflicts'
      //     };
      //   }
      //   importData.conflictResolution = resolution;
      // }

      // Import the data with simplified new-IDs-always approach
      const importResult = await this.importTripData(importData, context);

      return {
        success: true,
        tripId: importResult.tripId,
        tripName: importResult.tripName,
        itemsImported: importResult.itemsImported,
        documentsImported: importResult.documentsImported
      };

    } catch (error) {
      console.error('[TripImport] ZIP import failed:', error);
      throw error;
    }
  }

  /**
   * Process JSON file import
   *
   * @param {File} file - JSON file
   * @param {Object} context - Application context
   * @returns {Promise<Object>} Import result
   */
  async processJsonImport(file, context) {
    console.log('Processing JSON import...');

    // Read and parse JSON content
    const fileContent = await this.readFileAsText(file);
    let importData;

    try {
      importData = JSON.parse(fileContent);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (_error) {
      throw new Error('Invalid JSON format');
    }

    // Validate import data
    const validation = await this.validateImportData(importData);
    if (!validation.valid) {
      throw new Error(`Invalid import data: ${validation.error}`);
    }

    // Import the data (JSON imports typically don't include documents)
    const importResult = await this.importTripData(importData, context);

    return {
      success: true,
      tripId: importResult.tripId,
      tripName: importResult.tripName,
      itemsImported: importResult.itemsImported,
      documentsImported: 0
    };
  }

  /**
   * Read file content as text (for JSON files)
   *
   * @param {File} file - File to read
   * @returns {Promise<string>} File content
   */
  readFileAsText(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => resolve(event.target.result);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  }

  /**
   * Read file content as binary array buffer (for ZIP files)
   *
   * @param {File} file - File to read
   * @returns {Promise<ArrayBuffer>} File content as binary
   */
  readFileAsArrayBuffer(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => resolve(event.target.result);
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsArrayBuffer(file);
    });
  }

  /**
   * Parse ZIP content using real ZIP extraction
   *
   * @param {File} zipFile - ZIP file to extract
   * @returns {Promise<Object>} Parsed import data
   */
  async parseZipContent(zipFile) {
    console.log('[TripImport] Extracting ZIP file using ZipFormatProcessor...');

    try {
      // Load ZIP processor
      const processor = await loadZipProcessor();

      // Extract ZIP using production-ready processor
      const extractedData = await processor.extractTripImport(zipFile);

      console.log('[TripImport] ZIP extraction complete:', {
        hasManifest: !!extractedData.manifest,
        hasTripData: !!extractedData.tripData,
        hasDocumentInfo: !!extractedData.documentInfo,
        tripDataKeys: Object.keys(extractedData.tripData || {})
      });

      // Transform to controller's expected format
      return {
        exportMetadata: extractedData.manifest?.exportMetadata || {},
        trip: extractedData.tripData?.trip || {},
        days: extractedData.tripData?.days || [],
        places: extractedData.tripData?.places || [],
        accommodation: extractedData.tripData?.accommodation || [],
        travel: extractedData.tripData?.travel || [],
        documents: extractedData.tripData?.documents || [],
        links: extractedData.tripData?.links || [],
        _meta: extractedData.tripData?._meta,
        documentInfo: extractedData.documentInfo,
        zipInstance: extractedData.zipInstance // Keep for document file extraction
      };

    } catch (error) {
      console.error('[TripImport] ZIP extraction failed:', error);
      throw new Error(`Failed to extract ZIP file: ${error.message}`);
    }
  }

  /**
   * Validate import data structure and content
   *
   * @param {Object} importData - Data to validate
   * @returns {Promise<Object>} Validation result
   */
  async validateImportData(importData) {
    console.log('Validating import data...');

    // Check required structure
    if (!importData.trip) {
      return { valid: false, error: 'Missing trip information' };
    }

    if (!importData.trip.name) {
      return { valid: false, error: 'Trip name is required' };
    }

    // Check version compatibility
    if (importData.exportMetadata?.exportVersion &&
        !this.supportedVersions.includes(importData.exportMetadata.exportVersion)) {
      return {
        valid: false,
        error: `Unsupported export version: ${importData.exportMetadata.exportVersion}`
      };
    }

    // Validate data integrity
    if (importData.days && !Array.isArray(importData.days)) {
      return { valid: false, error: 'Invalid days data format' };
    }

    if (importData.places && !Array.isArray(importData.places)) {
      return { valid: false, error: 'Invalid places data format' };
    }

    return { valid: true };
  }

  /**
   * Check for conflicts with existing data
   *
   * @param {Object} importData - Import data
   * @param {Object} context - Application context
   * @returns {Promise<Object>} Conflict information
   */
  async checkForConflicts(importData, _context) {
    console.log('Checking for conflicts...');

    // TODO: Check against existing trips in the database
    // For now, simulate some potential conflicts

    const conflicts = [];

    // Check for trip name conflicts
    // const existingTrips = await tripService.fetchTrips(context.user.uid);
    // const nameConflict = existingTrips.find(trip => trip.name === importData.trip.name);

    // Simulated conflict check
    const hasNameConflict = Math.random() > 0.7; // 30% chance of conflict for demo

    if (hasNameConflict) {
      conflicts.push({
        type: 'trip_name',
        message: `A trip named "${importData.trip.name}" already exists`,
        resolution: 'rename'
      });
    }

    return {
      hasConflicts: conflicts.length > 0,
      conflicts: conflicts
    };
  }

  /**
   * Resolve conflicts with user input
   *
   * @param {Object} conflicts - Conflict information
   * @param {Object} context - Application context
   * @returns {Promise<Object>} Resolution result
   */
  async resolveConflicts(conflicts, _context) {
    console.log('Resolving conflicts...');

    // TODO: Show conflict resolution dialog to user
    // For now, automatically resolve by renaming

    const resolution = {
      proceed: true,
      actions: []
    };

    conflicts.conflicts.forEach(conflict => {
      if (conflict.type === 'trip_name') {
        resolution.actions.push({
          type: 'rename_trip',
          newName: `${conflict.originalName} (Imported ${new Date().toLocaleDateString()})`
        });
      }
    });

    return resolution;
  }

  /**
   * Import trip data into the database
   * SIMPLIFIED APPROACH: Always use NEW IDs (addDoc), never preserve original IDs
   *
   * @param {Object} importData - Validated import data
   * @param {Object} context - Application context
   * @returns {Promise<Object>} Import result
   */
  async importTripData(importData, context) {
    console.log('[TripImport] Starting import with simplified new-IDs approach...');

    const userId = context.user.uid;
    // Support both formats: importData.tripData.trip or importData.trip
    const tripData = importData.tripData || importData;
    const importTripId = tripData.trip.id;

    // Step 1: Check if trip exists for this user
    const existingTrip = await this.checkExistingTrip(userId, importTripId);

    // Step 2: If exists, confirm replacement
    if (existingTrip) {
      const confirmed = await this.confirmReplace(tripData.trip.name);
      if (!confirmed) {
        throw new Error('Import cancelled by user');
      }

      console.log(`[TripImport] Deleting existing trip: ${importTripId}`);
      await this.deleteExistingTrip(userId, importTripId);
    }

    // Step 3: Import with NEW IDs (always) - pass complete importData including zipInstance
    console.log('[TripImport] Importing trip with new IDs...');
    const dataToImport = {
      ...tripData,
      zipInstance: importData.zipInstance // Ensure zipInstance is passed through
    };
    const newTripId = await this.importWithNewIds(dataToImport, userId);

    // Step 4: Calculate import statistics
    const itemsImported = {
      days: tripData.days?.length || 0,
      places: tripData.places?.length || 0,
      accommodation: tripData.accommodation?.length || 0,
      travel: tripData.travel?.length || 0,
      documents: tripData.documents?.length || 0,
      links: tripData.links?.length || 0
    };

    console.log('[TripImport] Import complete:', {
      newTripId,
      itemsImported
    });

    return {
      tripId: newTripId,
      tripName: tripData.trip.name,
      itemsImported,
      documentsImported: importData.documents?.length || 0
    };
  }

  /**
   * Check if a trip with the given ID already exists for the user
   *
   * @param {string} userId - User ID
   * @param {string} tripId - Trip ID to check
   * @returns {Promise<Object|null>} Existing trip data or null
   */
  async checkExistingTrip(userId, tripId) {
    try {
      const tripRef = doc(db, 'users', userId, 'trips', tripId);
      const tripSnap = await getDoc(tripRef);
      return tripSnap.exists() ? tripSnap.data() : null;
    } catch (error) {
      console.warn('[TripImport] Error checking existing trip:', error);
      return null;
    }
  }

  /**
   * Confirm replacement of existing trip
   * TODO: Implement actual confirmation dialog in Phase 4
   *
   * @param {string} tripName - Name of trip to replace
   * @returns {Promise<boolean>} User confirmation
   */
  async confirmReplace(tripName) {
    // For now, return true (auto-confirm)
    // TODO: Implement actual confirmation dialog in Phase 4
    console.log(`[TripImport] Auto-confirming replacement of trip: ${tripName}`);
    console.log('[TripImport] TODO: Implement user confirmation dialog');
    return true;
  }

  /**
   * Delete existing trip using shared comprehensive deletion
   *
   * @param {string} userId - User ID
   * @param {string} tripId - Trip ID to delete
   * @returns {Promise<void>}
   */
  async deleteExistingTrip(userId, tripId) {
    console.log(`[TripImport] Deleting existing trip using shared deleteTrip: ${tripId}`);

    // Use shared comprehensive deletion function
    const { deleteTrip } = await import('@trip-planner-monorepo/shared');

    await deleteTrip(userId, tripId);

    console.log('[TripImport] Existing trip deleted via shared function');
  }

  /**
   * Import trip data with NEW IDs (core import logic)
   * This is the heart of the import process - creates all entities with fresh IDs
   *
   * @param {Object} importData - Import data
   * @param {string} userId - User ID
   * @returns {Promise<string>} New trip ID
   */
  async importWithNewIds(importData, userId) {
    const idMap = {}; // Maps old IDs → new IDs

    // Step 1: Create trip with new ID
    console.log('[TripImport] Creating trip...');
    const tripRef = await addDoc(collection(db, 'users', userId, 'trips'), {
      name: `${importData.trip.name} - imported`,
      description: importData.trip.description || '',
      startDate: this.convertTimestampToISOString(importData.trip.startDate),
      endDate: this.convertTimestampToISOString(importData.trip.endDate),
      destination: importData.trip.destination || '',
      travelers: importData.trip.travelers || 1,
      budget: importData.trip.budget || 0,
      currency: importData.trip.currency || 'USD',
      status: importData.trip.status || 'planned',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    });

    idMap[importData.trip.id] = tripRef.id;
    console.log(`[TripImport] Trip created: ${importData.trip.id} → ${tripRef.id}`);

    // Step 2: Import days (without contentBlocks initially)
    console.log(`[TripImport] Importing ${importData.days?.length || 0} days...`);
    for (const day of (importData.days || [])) {
      const dayRef = await addDoc(collection(db, 'users', userId, 'trips', tripRef.id, 'days'), {
        date: this.convertTimestampToISOString(day.date),
        title: day.title || '',
        order: day.order || 0,
        notes: day.notes || '',
        content: [], // Will remap contentBlocks later
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      idMap[day.id] = dayRef.id;
    }

    // Step 3: Import places
    console.log(`[TripImport] Importing ${importData.places?.length || 0} places...`);
    for (const place of (importData.places || [])) {
      const placeRef = await addDoc(collection(db, 'users', userId, 'trips', tripRef.id, 'places'), {
        name: place.name || '',
        address: place.address || '',
        city: place.city || '',
        country: place.country || '',
        context: place.context || '',
        type: place.type || '',
        latitude: place.latitude || null,
        longitude: place.longitude || null,
        resources: place.resources || [],
        userContent: place.userContent || '',
        contentBlocks: [], // Will remap later
        // AI-generated resources (added for AI resource export/import support)
        aiResources: place.aiResources || null,
        deletedAIResources: place.deletedAIResources || [],
        aiResourcesUpdatedAt: place.aiResourcesUpdatedAt || null,
        deletedAIResourcesUpdatedAt: place.deletedAIResourcesUpdatedAt || null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      idMap[place.id] = placeRef.id;
      console.log(`[TripImport] Place imported: ${place.id} → ${placeRef.id} (${place.name})`);

      // Log AI resource restoration for debugging
      if (place.aiResources) {
        const resourceCount = Object.values(place.aiResources)
          .reduce((sum, arr) => sum + (Array.isArray(arr) ? arr.length : 0), 0);
        console.log(`[TripImport]   └─ Imported ${resourceCount} AI resources`);
      }
      if (place.deletedAIResources && place.deletedAIResources.length > 0) {
        console.log(`[TripImport]   └─ Imported ${place.deletedAIResources.length} deleted AI resources`);
      }
    }

    // Step 4: Import accommodation
    console.log(`[TripImport] Importing ${importData.accommodation?.length || 0} accommodation...`);
    for (const accom of (importData.accommodation || [])) {
      // Convert dates in details object
      const details = { ...(accom.details || {}) };
      if (details.checkIn) {
        details.checkIn = this.convertTimestampToISOString(details.checkIn);
      }
      if (details.checkOut) {
        details.checkOut = this.convertTimestampToISOString(details.checkOut);
      }

      const accomRef = await addDoc(collection(db, 'users', userId, 'trips', tripRef.id, 'accommodation'), {
        type: accom.type || 'hotel',
        details, // Use converted details object
        summary: accom.summary || '',
        status: accom.status || 'planned',
        cost: accom.cost || null,
        notes: accom.notes || '',
        placeReference: null, // Will remap later if exists - CRITICAL for place linking
        contentBlocks: [], // Will remap later
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      idMap[accom.id] = accomRef.id;
      console.log(`[TripImport] Accommodation imported: ${accom.id} → ${accomRef.id} (${accom.details?.name || 'No name'})`);
    }

    // Step 5: Import travel
    console.log(`[TripImport] Importing ${importData.travel?.length || 0} travel...`);
    for (const travelItem of (importData.travel || [])) {
      const travelRef = await addDoc(collection(db, 'users', userId, 'trips', tripRef.id, 'travel'), {
        type: travelItem.type || 'flight',
        details: travelItem.details || {},
        summary: travelItem.summary || '',
        status: travelItem.status || 'planned',
        departureTime: this.convertTimestampToISOString(travelItem.departureTime),
        arrivalTime: this.convertTimestampToISOString(travelItem.arrivalTime),
        cost: travelItem.cost || null,
        notes: travelItem.notes || '',
        contentBlocks: [], // Will remap later
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
      idMap[travelItem.id] = travelRef.id;
      console.log(`[TripImport] Travel imported: ${travelItem.id} → ${travelRef.id} (${travelItem.type})`);
    }

    // Step 6: Upload document files to Firebase Storage and get new URLs
    console.log(`[TripImport] Importing ${importData.documents?.length || 0} documents...`);
    let documentStorageResults = {};

    if (importData.documents && importData.documents.length > 0 && importData.zipInstance) {
      console.log('[TripImport] Uploading document files to Firebase Storage...');
      documentStorageResults = await this.uploadDocumentFiles(
        importData.documents,
        importData.zipInstance,
        userId,
        tripRef.id
      );
    } else if (importData.documents && importData.documents.length > 0) {
      console.warn('[TripImport] ⚠️  No ZIP instance available - documents will be imported without files');
    }

    // Step 7: Create document Firestore records with new storage paths and URLs
    for (const document of (importData.documents || [])) {
      const oldDocId = document.firestoreId || document.id;

      // Get new storage path and download URL if upload was successful
      const newStorage = documentStorageResults[oldDocId];
      const storagePath = newStorage?.storagePath || '';
      const downloadUrl = newStorage?.downloadUrl || '';

      if (newStorage) {
        console.log(`[TripImport] ✅ Using NEW storage for document: ${document.name}`);
        console.log(`[TripImport]    New path: ${storagePath}`);
      } else {
        console.warn(`[TripImport] ⚠️  No file uploaded for document: ${document.name}`);
      }

      const docRef = await addDoc(collection(db, 'users', userId, 'trips', tripRef.id, 'documents'), {
        name: document.name || '',
        title: document.title || document.name || '',
        type: document.type || '',
        primaryCategory: document.primaryCategory || 'uncategorized',
        categories: document.categories || [],
        size: document.size || 0,
        mimeType: document.mimeType || '',
        uploadedAt: this.convertTimestampToISOString(document.uploadedAt),
        summary: document.summary || '',
        connections: [], // Will remap later - CRITICAL for entity associations
        contentBlocks: [], // Will remap later - CRITICAL for entity associations
        downloadUrl, // NEW download URL for this user
        storagePath, // NEW storage path for this user
        // Additional fields from original document
        originalName: document.originalName || document.name || '',
        storageId: newStorage?.storagePath ? `doc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}` : '',
        tripId: tripRef.id,
        userId: userId,
        uploadedBy: userId,
        processingStatus: document.processingStatus || 'completed',
        processingError: document.processingError || null,
        extractedData: document.extractedData || { extractedAt: null, documentType: null, confidence: null, data: {} },
        preview: document.preview || { confidence: null, format: null, type: 'placeholder', content: null, generatedAt: null },
        url: document.url || null,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });

      if (oldDocId) {
        idMap[oldDocId] = docRef.id;
        console.log(`[TripImport] Document imported: ${oldDocId} → ${docRef.id} (${document.title || document.name})`);
      } else {
        console.warn(`[TripImport] Document imported without firestoreId or id: → ${docRef.id} (${document.title || document.name})`);
      }
    }

    // Step 8: Remap all contentBlocks with new IDs
    console.log('[TripImport] Remapping contentBlocks...');
    await this.remapAllContentBlocks(userId, tripRef.id, importData, idMap);

    // Step 9: Import connection registry links (remapped ids + dedupKeys)
    if (importData.links?.length) {
      console.log(`[TripImport] Importing ${importData.links.length} connection registry links...`);
      const { connectionRegistryService } = await import('@trip-planner-monorepo/shared');
      const linkResult = await connectionRegistryService.importLinkDocuments(
        userId,
        tripRef.id,
        importData.links,
        idMap
      );
      console.log('[TripImport] Link import complete:', linkResult);
    }

    // Step 10: Restore or bootstrap link registry config
    const { linkRegistryConfigService } = await import('@trip-planner-monorepo/shared');
    if (importData._meta?.linkRegistry) {
      await linkRegistryConfigService.setLinkRegistryConfig(
        userId,
        tripRef.id,
        importData._meta.linkRegistry
      );
      console.log('[TripImport] Restored link registry config');
    } else {
      const bootstrap = await linkRegistryConfigService.bootstrapNewTripRegistry(
        userId,
        tripRef.id,
        { extractLegacyLinks: !importData.links?.length }
      );
      console.log('[TripImport] Applied new-trip link registry defaults:', bootstrap.config);
      if (bootstrap.extractResult) {
        console.log('[TripImport] Legacy link extraction complete:', bootstrap.extractResult.stats);
      }
    }

    return tripRef.id;
  }

  /**
   * Upload document binary files to Firebase Storage and get new download URLs
   * Creates completely independent document storage for the importing user
   *
   * @param {Array} documents - Array of document metadata from export
   * @param {Object} zipInstance - JSZip instance with document files
   * @param {string} userId - Importing user's ID
   * @param {string} tripId - New trip ID
   * @returns {Promise<Object>} Map of old document IDs to {storagePath, downloadUrl}
   */
  async uploadDocumentFiles(documents, zipInstance, userId, tripId) {
    console.log(`[TripImport] Uploading ${documents.length} document files to Firebase Storage...`);

    // DEBUG: List ALL files in the ZIP to understand structure
    console.log('[TripImport] ========================================');
    console.log('[TripImport] DEBUG: Files available in ZIP:');
    const zipFiles = [];
    zipInstance.forEach((relativePath, file) => {
      if (!file.dir) {
        zipFiles.push(relativePath);
        console.log(`[TripImport]   - ${relativePath}`);
      }
    });
    console.log(`[TripImport] Total files in ZIP: ${zipFiles.length}`);
    console.log('[TripImport] ========================================');

    const documentStorageResults = {};

    for (const document of documents) {
      try {
        const oldDocId = document.firestoreId || document.id;
        if (!oldDocId) {
          console.warn('[TripImport] Document missing ID, skipping file upload:', document.name);
          continue;
        }

        // DEBUG: Show all available properties on the document
        console.log('[TripImport] ----------------------------------------');
        console.log('[TripImport] Processing document:', {
          id: oldDocId,
          name: document.name,
          originalName: document.originalName,
          fileName: document.fileName,
          title: document.title,
          type: document.type,
          mimeType: document.mimeType,
          allKeys: Object.keys(document)
        });

        // Try to extract binary file from ZIP
        // Based on export code, files are added to documents/ or media/ folders
        // using fileData.originalName (which comes from docData.name || docData.originalName)
        const possiblePaths = [
          // Primary path: documents folder with name property
          `documents/${document.name}`,
          // Media folder for images
          `media/${document.name}`,
          // Fallback paths
          `documents/${document.originalName}`,
          `media/${document.originalName}`,
          `documents/${document.fileName}`,
          `media/${document.fileName}`,
          `documents/${document.title}`,
          `media/${document.title}`
        ].filter(Boolean);

        console.log('[TripImport] Trying paths:', possiblePaths);

        let fileData = null;
        

        for (const path of possiblePaths) {
          try {
            const zipEntry = zipInstance.file(path);
            if (zipEntry) {
              fileData = await zipEntry.async('arraybuffer');
              console.log(`[TripImport] ✅ Found document file at: ${path} (${fileData.byteLength} bytes)`);
              break;
            }
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          } catch (_err) {
            // Try next path
            console.log(`[TripImport]    ❌ Not found at: ${path}`);
          }
        }

        if (!fileData) {
          console.warn(`[TripImport] ⚠️  Document file not found in ZIP for: ${document.name}`);
          console.warn('[TripImport]    Tried paths:', possiblePaths);
          console.warn('[TripImport]    Available document/media files:',
            zipFiles.filter(f => f.startsWith('documents/') || f.startsWith('media/')));
          continue;
        }

        // Generate new storage ID and path for this user
        const newStorageId = `doc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        const fileName = document.name || document.originalName || 'document.pdf';
        const storagePath = `users/${userId}/trips/${tripId}/documents/${newStorageId}/${fileName}`;

        console.log(`[TripImport] Uploading: ${fileName} → ${storagePath}`);

        // Upload to Firebase Storage
        const storageRef = ref(storage, storagePath);
        const uploadResult = await uploadBytes(storageRef, fileData, {
          contentType: document.mimeType || 'application/octet-stream',
          customMetadata: {
            originalName: document.name || fileName,
            uploadedBy: userId,
            tripId: tripId
          }
        });

        // Get new download URL
        const downloadUrl = await getDownloadURL(uploadResult.ref);

        console.log(`[TripImport] ✅ Document uploaded successfully: ${fileName}`);
        console.log(`[TripImport]    Old ID: ${oldDocId}`);
        console.log(`[TripImport]    New path: ${storagePath}`);
        console.log(`[TripImport]    Download URL: ${downloadUrl.substring(0, 80)}...`);

        // Store results for this document
        documentStorageResults[oldDocId] = {
          storagePath,
          downloadUrl
        };

      } catch (error) {
        console.error(`[TripImport] ❌ Failed to upload document: ${document.name}`, error);
        // Continue with other documents even if one fails
      }
    }

    console.log(`[TripImport] Document upload complete: ${Object.keys(documentStorageResults).length}/${documents.length} successful`);
    return documentStorageResults;
  }

  /**
   * Remap all contentBlocks to use new entity IDs
   * Critical for maintaining relationships between entities
   *
   * @param {string} userId - User ID
   * @param {string} newTripId - New trip ID
   * @param {Object} importData - Original import data
   * @param {Object} idMap - Mapping of old IDs to new IDs
   * @returns {Promise<void>}
   */
  async remapAllContentBlocks(userId, newTripId, importData, idMap) {
    const batch = writeBatch(db);

    console.log('[TripImport] Starting contentBlock remapping...');
    console.log('[TripImport] ID Map keys:', Object.keys(idMap));

    // Remap days content
    console.log('[TripImport] Remapping days content...');
    for (const day of (importData.days || [])) {
      if (day.content && day.content.length > 0) {
        console.log(`[TripImport] Day ${day.id}: ${day.content.length} content blocks`);
        const remappedContent = this.remapContentBlockArray(day.content, idMap, 'day');
        const dayRef = doc(db, 'users', userId, 'trips', newTripId, 'days', idMap[day.id]);
        batch.update(dayRef, { content: remappedContent });
      }
    }

    // Remap accommodation contentBlocks AND placeReference
    console.log('[TripImport] Remapping accommodation contentBlocks and placeReference...');
    for (const accom of (importData.accommodation || [])) {
      const accomRef = doc(db, 'users', userId, 'trips', newTripId, 'accommodation', idMap[accom.id]);
      const updates = {};

      // Remap contentBlocks if present
      if (accom.contentBlocks && accom.contentBlocks.length > 0) {
        console.log(`[TripImport] Accommodation ${accom.id}: ${accom.contentBlocks.length} contentBlocks`);
        updates.contentBlocks = this.remapContentBlockArray(accom.contentBlocks, idMap, 'accommodation');
      }

      // Remap placeReference if present - CRITICAL FIX
      if (accom.placeReference && accom.placeReference.placeId) {
        const oldPlaceId = accom.placeReference.placeId;
        const newPlaceId = idMap[oldPlaceId];

        if (newPlaceId) {
          updates.placeReference = {
            placeId: newPlaceId,
            linkedAt: accom.placeReference.linkedAt || new Date().toISOString()
          };
          console.log(`[TripImport] ✅ Accommodation ${accom.id} placeReference: ${oldPlaceId} → ${newPlaceId}`);
        } else {
          console.warn(`[TripImport] ⚠️  Accommodation ${accom.id} placeReference: ${oldPlaceId} NOT FOUND in idMap`);
        }
      }

      // Apply updates if any
      if (Object.keys(updates).length > 0) {
        batch.update(accomRef, updates);
      }
    }

    // Remap places contentBlocks
    console.log('[TripImport] Remapping places contentBlocks...');
    for (const place of (importData.places || [])) {
      if (place.contentBlocks && place.contentBlocks.length > 0) {
        console.log(`[TripImport] Place ${place.id} (${place.name}): ${place.contentBlocks.length} contentBlocks`);
        const remappedBlocks = this.remapContentBlockArray(place.contentBlocks, idMap, 'place');
        const placeRef = doc(db, 'users', userId, 'trips', newTripId, 'places', idMap[place.id]);
        batch.update(placeRef, { contentBlocks: remappedBlocks });
      }
    }

    // Remap travel contentBlocks
    console.log('[TripImport] Remapping travel contentBlocks...');
    for (const travelItem of (importData.travel || [])) {
      if (travelItem.contentBlocks && travelItem.contentBlocks.length > 0) {
        console.log(`[TripImport] Travel ${travelItem.id}: ${travelItem.contentBlocks.length} contentBlocks`);
        const remappedBlocks = this.remapContentBlockArray(travelItem.contentBlocks, idMap, 'travel');
        const travelRef = doc(db, 'users', userId, 'trips', newTripId, 'travel', idMap[travelItem.id]);
        batch.update(travelRef, { contentBlocks: remappedBlocks });
      }
    }

    // Remap document contentBlocks AND connections - CRITICAL FIX for accommodation/entity associations
    console.log('[TripImport] Remapping document contentBlocks and connections...');
    for (const document of (importData.documents || [])) {
      const oldDocId = document.firestoreId || document.id;
      const docRef = doc(db, 'users', userId, 'trips', newTripId, 'documents', idMap[oldDocId]);
      const updates = {};

      // Remap contentBlocks if present
      if (document.contentBlocks && document.contentBlocks.length > 0) {
        console.log(`[TripImport] Document ${oldDocId} (${document.title || document.name}): ${document.contentBlocks.length} contentBlocks`);
        updates.contentBlocks = this.remapContentBlockArray(document.contentBlocks, idMap, 'document');
      }

      // Remap connections array - CRITICAL FIX
      if (document.connections && document.connections.length > 0) {
        console.log(`[TripImport] Document ${oldDocId} (${document.title || document.name}): ${document.connections.length} connections`);
        updates.connections = this.remapConnectionsArray(document.connections, idMap);
      }

      // Apply updates if any
      if (Object.keys(updates).length > 0) {
        batch.update(docRef, updates);
      }
    }

    await batch.commit();
    console.log('[TripImport] ContentBlocks remapped successfully');
  }

  /**
   * Remap a single array of contentBlocks to use new entity IDs
   * Handles all contentBlock types and their entity references
   *
   * @param {Array} contentBlocks - Array of contentBlocks
   * @param {Object} idMap - Mapping of old IDs to new IDs
   * @param {string} entityType - Type of entity for logging context
   * @returns {Array} Remapped contentBlocks
   */
  remapContentBlockArray(contentBlocks, idMap, entityType = 'unknown') {
    return contentBlocks.map((block, index) => {
      const remappedBlock = { ...block };

      console.log(`[ContentBlock Remap] ${entityType} block ${index}: type=${block.type}, keys=${Object.keys(block).join(',')}`);

      // Remap entity IDs based on block type
      if (block.type === 'accommodation_reference' && block.accommodationId) {
        const oldId = block.accommodationId;
        const newId = idMap[block.accommodationId];

        if (newId) {
          remappedBlock.accommodationId = newId;
          console.log(`[ContentBlock Remap] ✅ ${entityType} accommodation_reference: ${oldId} → ${newId}`);
        } else {
          console.warn(`[ContentBlock Remap] ⚠️ ${entityType} accommodation_reference: ${oldId} NOT FOUND in idMap`);
          remappedBlock.accommodationId = block.accommodationId; // Keep old ID as fallback
        }

        // CRITICAL: Also remap placeId within accommodation blocks
        if (block.placeId) {
          const oldPlaceId = block.placeId;
          const newPlaceId = idMap[block.placeId];

          if (newPlaceId) {
            remappedBlock.placeId = newPlaceId;
            console.log(`[ContentBlock Remap] ✅ ${entityType} placeId within accommodation: ${oldPlaceId} → ${newPlaceId}`);
          } else {
            console.warn(`[ContentBlock Remap] ⚠️ ${entityType} placeId within accommodation: ${oldPlaceId} NOT FOUND in idMap`);
            remappedBlock.placeId = block.placeId;
          }
        }
      }

      if (block.type === 'place_reference' && block.placeId) {
        const oldId = block.placeId;
        const newId = idMap[block.placeId];

        if (newId) {
          remappedBlock.placeId = newId;
          console.log(`[ContentBlock Remap] ✅ ${entityType} place_reference: ${oldId} → ${newId}`);
        } else {
          console.warn(`[ContentBlock Remap] ⚠️ ${entityType} place_reference: ${oldId} NOT FOUND in idMap`);
          remappedBlock.placeId = block.placeId;
        }
      }

      if (block.type === 'travel_reference' && block.travelId) {
        const oldId = block.travelId;
        const newId = idMap[block.travelId];

        if (newId) {
          remappedBlock.travelId = newId;
          console.log(`[ContentBlock Remap] ✅ ${entityType} travel_reference: ${oldId} → ${newId}`);
        } else {
          console.warn(`[ContentBlock Remap] ⚠️ ${entityType} travel_reference: ${oldId} NOT FOUND in idMap`);
          remappedBlock.travelId = block.travelId;
        }
      }

      if (block.type === 'document_reference' && block.documentId) {
        const oldId = block.documentId;
        const newId = idMap[block.documentId];

        if (newId) {
          remappedBlock.documentId = newId;
          console.log(`[ContentBlock Remap] ✅ ${entityType} document_reference: ${oldId} → ${newId}`);
        } else {
          console.warn(`[ContentBlock Remap] ⚠️ ${entityType} document_reference: ${oldId} NOT FOUND in idMap`);
          remappedBlock.documentId = block.documentId;
        }
      }

      // Remap relatedDayIds if present
      if (block.relatedDayIds && Array.isArray(block.relatedDayIds)) {
        remappedBlock.relatedDayIds = block.relatedDayIds.map(dayId => {
          const newDayId = idMap[dayId];
          if (!newDayId) {
            console.warn(`[ContentBlock Remap] ⚠️ ${entityType} relatedDayId: ${dayId} NOT FOUND in idMap`);
          }
          return newDayId || dayId;
        });
        console.log(`[ContentBlock Remap] ${entityType} relatedDayIds: ${block.relatedDayIds.join(',')} → ${remappedBlock.relatedDayIds.join(',')}`);
      }

      // Remap relatedEntityId if present
      if (block.relatedEntityId) {
        const oldId = block.relatedEntityId;
        const newId = idMap[block.relatedEntityId];

        if (newId) {
          remappedBlock.relatedEntityId = newId;
          console.log(`[ContentBlock Remap] ✅ ${entityType} relatedEntityId: ${oldId} → ${newId}`);
        } else {
          console.warn(`[ContentBlock Remap] ⚠️ ${entityType} relatedEntityId: ${oldId} NOT FOUND in idMap`);
          remappedBlock.relatedEntityId = block.relatedEntityId;
        }
      }

      // Remap sourceEntityId if present - CRITICAL for Place → Accommodation/Tour/Activity links
      if (block.sourceEntityId) {
        const oldId = block.sourceEntityId;
        const newId = idMap[block.sourceEntityId];

        if (newId) {
          remappedBlock.sourceEntityId = newId;
          console.log(`[ContentBlock Remap] ✅ ${entityType} sourceEntityId (${block.sourceEntityType || 'unknown'}): ${oldId} → ${newId}`);
        } else {
          console.warn(`[ContentBlock Remap] ⚠️ ${entityType} sourceEntityId (${block.sourceEntityType || 'unknown'}): ${oldId} NOT FOUND in idMap`);
          remappedBlock.sourceEntityId = block.sourceEntityId;
        }
      }

      // Remap standalone dayId if present (used in accommodation contentBlocks)
      if (block.dayId) {
        const oldId = block.dayId;
        const newId = idMap[block.dayId];

        if (newId) {
          remappedBlock.dayId = newId;
          console.log(`[ContentBlock Remap] ✅ ${entityType} dayId: ${oldId} → ${newId}`);
        } else {
          console.warn(`[ContentBlock Remap] ⚠️ ${entityType} dayId: ${oldId} NOT FOUND in idMap`);
          remappedBlock.dayId = block.dayId;
        }
      }

      return remappedBlock;
    });
  }

  /**
   * Remap connections array to use new entity IDs
   * Used for document connections which reference other entities
   *
   * @param {Array} connections - Array of connection objects
   * @param {Object} idMap - Mapping of old IDs to new IDs
   * @returns {Array} Remapped connections
   */
  remapConnectionsArray(connections, idMap) {
    if (!connections || connections.length === 0) {
      return [];
    }

    return connections.map((connection, index) => {
      const remappedConnection = { ...connection };

      console.log(`[Connection Remap] Connection ${index}: entityType=${connection.entityType}, entityId=${connection.entityId}`);

      // Remap entityId based on entityType
      if (connection.entityId) {
        const oldId = connection.entityId;
        const newId = idMap[connection.entityId];

        if (newId) {
          remappedConnection.entityId = newId;
          console.log(`[Connection Remap] ✅ ${connection.entityType} connection: ${oldId} → ${newId}`);
        } else {
          console.warn(`[Connection Remap] ⚠️  ${connection.entityType} connection: ${oldId} NOT FOUND in idMap`);
          remappedConnection.entityId = connection.entityId; // Keep old ID as fallback
        }
      }

      return remappedConnection;
    });
  }

  // Helper methods

  validateImportContext(context) {
    return !!(context.user);
  }

  // Notification methods

  showImportStartedNotification(filename) {
    console.log(`Starting import from ${filename}...`);
  }

  showImportCompletedNotification(result) {
    console.log(`Import completed successfully: ${result.tripName}`);
    console.log(`Imported ${Object.values(result.itemsImported).reduce((a, b) => a + b, 0)} items`);
  }

  showImportErrorNotification(errorMessage) {
    console.error('Import error:', errorMessage);
  }

  /**
   * Check if import functionality is available
   *
   * @param {Object} context - Application context
   * @returns {boolean} Whether import is available
   */
  isImportAvailable(context) {
    return this.validateImportContext(context);
  }

  /**
   * Get supported file formats
   *
   * @returns {Array} Supported formats
   */
  getSupportedFormats() {
    return this.supportedFormats.map(format => ({
      extension: format,
      description: format === 'zip' ? 'Complete trip backup' : 'Trip data only',
      mimeType: format === 'zip' ? 'application/zip' : 'application/json'
    }));
  }

  /**
   * Get maximum file size in human-readable format
   *
   * @returns {string} Maximum file size
   */
  getMaxFileSizeString() {
    return `${Math.round(this.maxFileSize / 1024 / 1024)}MB`;
  }
}

// Export singleton instance
export const tripImportController = new TripImportController();
export default tripImportController;